import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useAuth } from '@/contexts/useAuth';
import { useSubscription } from '@/contexts/useSubscription';
import MembershipCard from '@/components/subscription/MembershipCard';
import Button from '@/components/ui/Button';
import Confetti from '@/components/ui/Confetti';

const RETRY_DELAY_MS = 1500;
const ease = [0.22, 1, 0.36, 1] as const;

const PREMIUM_UNLOCKS = [
  'Unlimited mock interviews',
  'Full practice mode with all 128 civics questions',
  'Detailed per-question review and explanations',
];

export default function SuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile } = useAuth();
  const { isPremium, cadence, currentPeriodEnd, refreshSubscription } =
    useSubscription();

  const sessionId = searchParams.get('session_id');

  const [resolving, setResolving] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const retriedRef = useRef(false);

  useEffect(() => {
    let active = true;

    const resolve = async () => {
      await refreshSubscription();
      if (!active) return;

      if (!retriedRef.current) {
        retriedRef.current = true;
        setTimeout(async () => {
          if (!active) return;
          await refreshSubscription();
          if (active) setResolving(false);
        }, RETRY_DELAY_MS);
        return;
      }

      setResolving(false);
    };

    resolve();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isPremium || resolving || !sessionId) return;
    const key = `confetti_seen_${sessionId}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    setShowConfetti(true);
    const timer = setTimeout(() => setShowConfetti(false), 1700);
    return () => clearTimeout(timer);
  }, [isPremium, resolving, sessionId]);

  const fullName = profile?.full_name?.trim() || 'Member';
  const firstName = fullName.split(' ')[0];

  const renewDate =
    isPremium && currentPeriodEnd
      ? new Date(currentPeriodEnd).toLocaleDateString(undefined, {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })
      : null;

  const memberSince = (() => {
    const source = profile?.created_at ?? user?.created_at;
    if (!source) return null;
    return new Date(source).toLocaleDateString(undefined, {
      month: 'short',
      year: 'numeric',
    });
  })();

  const planLabel =
    isPremium && cadence
      ? `Premium · ${cadence === 'annual' ? 'Annual' : 'Monthly'}`
      : 'Premium';

  return (
    <div className="relative min-h-screen overflow-hidden bg-cream flex items-center justify-center px-6 py-12">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[600px]"
        style={{
          background:
            'radial-gradient(ellipse 700px 400px at 50% 0%, rgba(212,163,115,0.22), transparent 65%)',
        }}
      />

      {showConfetti && <Confetti />}

      <div className="relative w-full max-w-md lg:max-w-xl text-center">
        <AnimatePresence mode="wait">
          {resolving ? (
            <motion.div
              key="activating"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, ease }}
            >
              <p className="font-sans text-xs uppercase tracking-[0.2em] text-text-muted mb-6">
                Welcome, {firstName}
              </p>

              <motion.div
                aria-hidden
                className="mx-auto mb-8 w-3 h-3 rounded-full bg-primary"
                animate={{ scale: [1, 1.4, 1], opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
              />

              <h1 className="font-display italic font-light text-4xl md:text-5xl text-text-primary leading-[1.1] mb-4">
                Activating your membership.
              </h1>

              <p className="font-sans text-sm text-text-muted">
                One moment while we confirm your subscription.
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="celebration"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, ease }}
            >
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.05, ease }}
                className="font-sans text-xs uppercase tracking-[0.2em] text-text-muted mb-3"
              >
                Welcome, {firstName}
              </motion.p>

              <motion.h1
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2, ease }}
                className="font-display italic font-light text-5xl md:text-6xl text-text-primary leading-[1.05] mb-8 md:mb-10"
              >
                You&apos;re <span className="text-primary">Premium.</span>
              </motion.h1>

              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3, ease }}
              >
                <MembershipCard
                  isPremium={isPremium}
                  fullName={fullName}
                  memberSince={memberSince}
                  planLabel={planLabel}
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.45, ease }}
                className="mt-8 md:mt-10 mx-auto max-w-[420px] text-left"
              >
                <div className="font-sans text-xs uppercase tracking-[0.2em] text-text-muted mb-3">
                  What you&apos;ve unlocked
                </div>
                <ul className="space-y-2.5">
                  {PREMIUM_UNLOCKS.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <div className="flex items-center justify-center w-5 h-5 rounded-full bg-success/15 mt-0.5 shrink-0">
                        <Check size={12} strokeWidth={3} className="text-success" />
                      </div>
                      <span className="font-sans text-sm text-text-secondary leading-relaxed">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6, ease }}
                className="mt-8 md:mt-10 mx-auto max-w-[420px]"
              >
                <Button
                  fullWidth
                  onClick={() => {
                    sessionStorage.setItem('citizenly_just_upgraded', '1');
                    navigate('/home', { replace: true });
                  }}
                >
                  Get started
                </Button>
              </motion.div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.78 }}
                className="font-sans text-xs text-text-muted mt-6"
              >
                {renewDate && <>Renews {renewDate}</>}
                {renewDate && user?.email && (
                  <span className="mx-2 text-text-muted/60">·</span>
                )}
                {user?.email && <>Receipt sent to {user.email}</>}
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
